import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject
} from '@angular/core';

import { UmssIconComponent } from '../icon/icon.component';
import { UmssSliderButtonComponent } from '../slider-button/slider-button.component';
import { BreakpointService } from '../../services/breakpoint.service';

@Component({
  selector: 'umss-button',
  standalone: true,
  imports: [CommonModule, UmssIconComponent, UmssSliderButtonComponent],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss']
})
export class UmssButtonComponent {
  @HostBinding('style.display') display = 'contents';

  private readonly breakpointService = inject(BreakpointService);
  /** Content props - Clean and semantic */
  @Input() label: string = '';

  /** Icon props - Direct Phosphor icon integration */
  @Input() leftIconName?: string; // Phosphor icon name for left icon (e.g., 'phosphorCheck')
  @Input() rightIconName?: string; // Phosphor icon name for right icon (e.g., 'phosphorArrowRight')
  @Input() iconSize?: string; // Icon size (default based on button size)
  @Input() iconColor?: string; // Icon color (defaults to currentColor)
  @Input() type: 'button' | 'reset' | 'submit' = 'button';
  @Input() href?: string;
  @Input() target: string = '_blank';

  /** Variant props - Well-typed and comprehensive */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() variant:
    | 'Primary'
    | 'Secondary'
    | 'Destructive'
    | 'Ghost'
    | 'Outline' = 'Primary';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;

  /** Layout props - Simple and flexible */
  @Input() fullWidth: boolean = false;
  @Input() iconOnly: boolean = false;
  @Input() slidable: 'always' | 'onlyMobile' | false = false;

  /** Events - Proper event handling */
  @Output() onClick = new EventEmitter<MouseEvent>();

  /** Computed properties */
  get isSlidable(): boolean {
    if (this.slidable === 'always') return true;
    if (this.slidable === 'onlyMobile')
      return this.breakpointService.isMobile();
    return false;
  }

  get showLeftIcon(): boolean {
    return !!this.leftIconName && !this.loading;
  }

  get showRightIcon(): boolean {
    return !!this.rightIconName && !this.loading;
  }

  get showLabel(): boolean {
    return !!this.label && !this.iconOnly;
  }

  get computedIconSize(): string {
    if (this.iconSize) return this.iconSize;

    // Default icon sizes based on button size
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Regular':
        return '20px';
      case 'Large':
        return '24px';
      default:
        return '20px';
    }
  }

  get buttonClasses(): string {
    const classes = ['refined-button'];

    if (this.fullWidth) classes.push('full-width');
    if (this.iconOnly) classes.push('icon-only');
    if (this.loading) classes.push('loading');

    return classes.join(' ');
  }

  onButtonClick(event: MouseEvent) {
    this.executeIfNotDisabled(event);
  }

  onSliderConfirmed() {
    this.executeIfNotDisabled();
  }

  private executeIfNotDisabled(event?: MouseEvent) {
    if (!this.disabled && !this.loading) this.onClick.emit(event);
  }
}
